import { get, post, put, del } from "@/lib/api";
import type {
  CompanyInfo,
  StatusResponse,
  DashboardItem,
  DashboardParams,
  TaskItem,
  ShiftResponse,
  ShiftParams,
  ChatRoomListItem,
  ChatMessageListResponse,
  SendChatMessageRequest,
  SendChatMessageResponse,
  EditChatMessageRequest,
  ChatUploadURLRequest,
  ChatUploadURLResponse,
  CreateGroupChatRequest,
  CreateGroupChatResponse,
  UpdateChatMembersRequest,
  ChatMemberItem,
  ChatUserItem,
  DriverItem,
  LogResponse,
  LogParams,
  NotificationUpdateResponse,
  LocationRequest,
} from "@/types/staff_api_def";

// ■■■■■■■ API通信層 ■■■■■■■

// GET /api/staff/company-info - 所属企業基礎情報取得
export const getCompanyInfo = async (): Promise<CompanyInfo> => {
  return await get<CompanyInfo>("/api/staff/company-info");
};

// GET /api/staff/dashboard - ダッシュボード情報取得
export const getDashboard = async (params?: DashboardParams): Promise<DashboardItem[]> => {
  return await get<DashboardItem[]>("/api/staff/dashboard", params);
};

// POST /api/staff/dashboard/read/:notificationId - ダッシュボード通知既読アクション
export const postDashboardRead = async (notificationId: string): Promise<StatusResponse> => {
  return await post<StatusResponse>(`/api/staff/dashboard/read/${notificationId}`, {});
};

// GET /api/staff/tasks - タスクボックス情報取得
export const getTasks = async (): Promise<TaskItem[]> => {
  return await get<TaskItem[]>("/api/staff/tasks");
};

// POST /api/staff/task/confirmed/:taskId - タスクの確認アクション
export const postTaskConfirmed = async (taskId: string): Promise<StatusResponse> => {
  return await post<StatusResponse>(`/api/staff/task/confirmed/${taskId}`, {});
};

// GET /api/staff/shift - エリア別シフト情報取得
export const getShift = async (params?: ShiftParams): Promise<ShiftResponse> => {
  return await get<ShiftResponse>("/api/staff/shift", params);
};

// POST /api/staff/shift/register - シフト登録
export const postShiftRegister = async (params: {
  date: string;
  shiftType: string;
  startTime?: string;
  endTime?: string;
  notes?: string;
  leaveNumber?: string;
}): Promise<StatusResponse> => {
  return await post<StatusResponse>("/api/staff/shift/register", params);
};

// POST /api/staff/shift/delete - シフト削除（本人の同日シフトを取消）
export const postShiftDelete = async (params: { date: string }): Promise<StatusResponse> => {
  return await post<StatusResponse>("/api/staff/shift/delete", params);
};

// GET /api/staff/drivers - ドライバー一覧取得
export const getDrivers = async (): Promise<DriverItem[]> => {
  return await get<DriverItem[]>("/api/staff/drivers");
};

// ■■■■■■■ チャットAPI ■■■■■■■

export const getChatRooms = async (): Promise<ChatRoomListItem[]> => {
  return await get<ChatRoomListItem[]>("/api/staff/chat/rooms");
};

export const postCreateChatRoom = async (params: CreateGroupChatRequest): Promise<CreateGroupChatResponse> => {
  return await post<CreateGroupChatResponse>("/api/staff/chat/rooms", params);
};

export const getChatMessages = async (roomId: string, params?: { cursor?: string; direction?: "before" | "after"; limit?: number }): Promise<ChatMessageListResponse> => {
  return await get<ChatMessageListResponse>(`/api/staff/chat/rooms/${roomId}/messages`, params);
};

export const postChatMessage = async (roomId: string, params: SendChatMessageRequest): Promise<SendChatMessageResponse> => {
  return await post<SendChatMessageResponse>(`/api/staff/chat/rooms/${roomId}/messages`, params);
};

export const putChatMessage = async (messageId: string, params: EditChatMessageRequest): Promise<StatusResponse> => {
  return await put<StatusResponse>(`/api/staff/chat/messages/${messageId}`, params);
};

export const deleteChatMessage = async (messageId: string): Promise<StatusResponse> => {
  return await del<StatusResponse>(`/api/staff/chat/messages/${messageId}`);
};

export const postChatRoomRead = async (roomId: string): Promise<StatusResponse> => {
  return await post<StatusResponse>(`/api/staff/chat/rooms/${roomId}/read`, {});
};

export const putChatRoomPin = async (roomId: string): Promise<StatusResponse> => {
  return await put<StatusResponse>(`/api/staff/chat/rooms/${roomId}/pin`, {});
};

export const putChatRoomMembers = async (roomId: string, params: UpdateChatMembersRequest): Promise<StatusResponse> => {
  return await put<StatusResponse>(`/api/staff/chat/rooms/${roomId}/members`, params);
};

export const getChatRoomMembers = async (roomId: string): Promise<ChatMemberItem[]> => {
  return await get<ChatMemberItem[]>(`/api/staff/chat/rooms/${roomId}/members`);
};

export const getChatUsers = async (): Promise<ChatUserItem[]> => {
  return await get<ChatUserItem[]>("/api/staff/chat/users");
};

export const postChatUploadURL = async (params: ChatUploadURLRequest): Promise<ChatUploadURLResponse> => {
  return await post<ChatUploadURLResponse>("/api/staff/chat/upload-url", params);
};

// GET /api/staff/log - スタッフ操作ログ取得
// date（業務日）を指定するとその1日ぶんだけ返る。指定しなければ当日ぶん。
export const getLog = async (params?: LogParams): Promise<LogResponse> => {
  return await get<LogResponse>("/api/staff/log", params);
};

// ■■■■■■■ Notification API通信層 ■■■■■■■

// GET /api/notification/update - 対象ユーザに新規通知があるかを確認する(ロングポーリング形式)
export const getNotificationUpdate = async (): Promise<NotificationUpdateResponse> => {
  return await get<NotificationUpdateResponse>("/api/notification/update");
};

// POST /api/notification/location - ユーザの現在位置をサーバに通知する
export const postLocation = async (params: LocationRequest): Promise<StatusResponse> => {
  return await post<StatusResponse>("/api/notification/location", params);
};

// POST /api/notification/location/stop - 位置トラッキングを停止する（退勤）
export const postLocationStop = async (): Promise<StatusResponse> => {
  return await post<StatusResponse>("/api/notification/location/stop", {});
};

// GET /api/staff/has-incomplete-task - 当日に未完了の割当タスクがあるか（退勤/シフト変更前の警告判定用）
export const getHasIncompleteTask = async (): Promise<{ hasIncompleteTask: boolean }> => {
  return await get<{ hasIncompleteTask: boolean }>("/api/staff/has-incomplete-task");
};
